import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Modal state management slice
 * Provides centralized control for modal open/close state across the application
 * Ensures underlying page state preservation and proper focus management
 */

export interface ModalState {
  // Track which modals are currently open
  openModals: Record<string, boolean>;
  // Store modal-specific data (e.g., editing entity, form data)
  modalData: Record<string, any>;
  // Track the last focused element before modal opened (for focus restoration)
  lastFocusedElement: Record<string, string | null>;
}

const initialState: ModalState = {
  openModals: {},
  modalData: {},
  lastFocusedElement: {},
};

const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    /**
     * Open a modal by its identifier
     * Optionally store data associated with the modal (e.g., entity being edited)
     */
    openModal: (
      state,
      action: PayloadAction<{ modalId: string; data?: any; focusedElement?: string }>
    ) => {
      const { modalId, data, focusedElement } = action.payload;
      state.openModals[modalId] = true;
      
      if (data !== undefined) {
        state.modalData[modalId] = data;
      }
      
      // Store the currently focused element for restoration on close
      if (focusedElement) {
        state.lastFocusedElement[modalId] = focusedElement;
      }
    },

    /**
     * Close a modal by its identifier
     * Clears associated data and focus tracking
     */
    closeModal: (state, action: PayloadAction<string>) => {
      const modalId = action.payload;
      state.openModals[modalId] = false;
      
      // Clear modal data when closing
      delete state.modalData[modalId];
      
      // Clear focus tracking (actual focus restoration happens in component)
      delete state.lastFocusedElement[modalId];
    },

    /**
     * Update data for an open modal without closing it
     * Useful for multi-step forms or dynamic modal content
     */
    updateModalData: (
      state,
      action: PayloadAction<{ modalId: string; data: any }>
    ) => {
      const { modalId, data } = action.payload;
      state.modalData[modalId] = data;
    },

    /**
     * Close all open modals
     * Useful for navigation or logout scenarios
     */
    closeAllModals: (state) => {
      state.openModals = {};
      state.modalData = {};
      state.lastFocusedElement = {};
    },

    /**
     * Check if a specific modal is open
     * This is a selector-like action but kept for consistency
     */
    toggleModal: (state, action: PayloadAction<string>) => {
      const modalId = action.payload;
      const isCurrentlyOpen = state.openModals[modalId];
      
      if (isCurrentlyOpen) {
        state.openModals[modalId] = false;
        delete state.modalData[modalId];
        delete state.lastFocusedElement[modalId];
      } else {
        state.openModals[modalId] = true;
      }
    },
  },
});

export const {
  openModal,
  closeModal,
  updateModalData,
  closeAllModals,
  toggleModal,
} = modalSlice.actions;

export default modalSlice.reducer;

// Selectors for easy access to modal state
export const selectIsModalOpen = (state: { modal: ModalState }, modalId: string): boolean => {
  return state.modal.openModals[modalId] || false;
};

export const selectModalData = (state: { modal: ModalState }, modalId: string): any => {
  return state.modal.modalData[modalId];
};

export const selectLastFocusedElement = (
  state: { modal: ModalState },
  modalId: string
): string | null => {
  return state.modal.lastFocusedElement[modalId] || null;
};

export const selectAnyModalOpen = (state: { modal: ModalState }): boolean => {
  return Object.values(state.modal.openModals).some((isOpen) => isOpen);
};
